import { CharacterStateMachine } from '../character/character-state-machine';
import type { CharacterState } from '../character/character-state';
import { createCharacterRuntimeState } from '../character/runtime-state';
import type { CharacterRuntimeState } from '../character/runtime-state';
import { TransitionReason } from '../character/transition';
import type { TransitionRequest } from '../character/transition';
import { assertInputFrame, createInputFrame, EMPTY_INPUT_FRAME } from '../input/input-frame';
import type { BufferedAction, InputFrame } from '../input/input-frame';
import { resolveInput } from '../input/input-resolver';
import type { ResolvedInput } from '../input/input-resolver';
import { assertLocalAabb, getAabbOverlap, toWorldAabb } from '../physics/aabb';
import type { LocalAabb } from '../physics/aabb';
import { resolveBodyPushAndWalls, updateFacing } from '../physics/collision-resolver';
import type { PushBody } from '../physics/collision-resolver';
import {
  assertSpatialDefinition,
  assertStageBounds,
  createTrapSpatialState,
  DEFAULT_SPATIAL_DEFINITION,
  DEFAULT_STAGE,
} from '../physics/spatial-types';
import type { CharacterSpatialDefinition, StageBounds, TrapSpatialState } from '../physics/spatial-types';
import { assertFiniteVec2 } from '../physics/vec2';
import type { Vec2 } from '../physics/vec2';
import {
  assertAttackDefinition,
  assertTrapDefinition,
  cloneAttackDefinition,
  createAttackInstance,
  getAttackPhase,
  getAttackTotalFrames,
  isAttackInvulnerable,
  isCancelRouteEligible,
  makeHitRecordKey,
  projectActiveAttackHitboxes,
  resolveAttackForHit,
} from '../combat/attack';
import type {
  AttackDefinition,
  EffectDefinition,
  EffectTrigger,
  GenericTrapDefinition,
  ProjectedAttackHitbox,
} from '../combat/attack';
import {
  calculateScaledDamage,
  classifyPendingHit,
  sortPendingHitsCanonically,
} from '../combat/resolution';
import type { CombatResult, PendingHit } from '../combat/resolution';
import {
  clampGauge,
  createCombatTrapRuntime,
  createComboRuntime,
  makeOncePerComboKey,
  resolveRoundResult,
} from '../combat/systems';
import type { CombatEffectRuntime, ComboRuntime } from '../combat/systems';
import { Mulberry32 } from '../random/mulberry32';
import { NormalCpuController } from '../cpu/normal-cpu';
import type { CpuAction, CpuProfile, CpuRuntimeState } from '../cpu/normal-cpu';
import { assertNonNegativeInteger, assertUint32 } from '../shared/invariants';
import { BattleEventType, createBattleEvent } from './battle-event';
import type { BattleEvent, BattleEventValue } from './battle-event';
import { canTransitionBattlePhase, isBattlePhase } from './battle-phase';
import type { BattlePhase } from './battle-phase';
import { cloneBattleRuntimeState } from './battle-runtime-state';
import type { BattleRuntimeState } from './battle-runtime-state';
import { projectBattleSnapshot } from './battle-snapshot';
import type { BattleSnapshot } from './battle-snapshot';
import type { DebugBox, DebugSnapshot } from './debug-snapshot';
import { CombatDiagnostics, projectCombatCharacter } from './combat-diagnostics';

export const INITIAL_TIMER_FRAMES = 3_600;
export type PlayerIndex = 0 | 1;

export interface BattleCoreConfig {
  readonly seed: number;
  readonly characterIds?: readonly [string, string];
  readonly characterDefinitionIds?: readonly [string, string];
  readonly stage?: StageBounds;
  readonly spatialDefinitions?: readonly [CharacterSpatialDefinition, CharacterSpatialDefinition];
  readonly initialPositions?: readonly [Vec2, Vec2];
  readonly maxHp?: readonly [number, number];
  readonly initialHp?: readonly [number, number];
  readonly initialGauge?: readonly [number, number];
  readonly initialTimerFrames?: number;
  readonly hurtboxes?: readonly [LocalAabb, LocalAabb];
  readonly hurtboxProfiles?: readonly [HurtboxProfile, HurtboxProfile];
  readonly attacks?: readonly AttackDefinition[];
  readonly attackBindings?: readonly [Partial<Record<BufferedAction, string>>, Partial<Record<BufferedAction, string>>];
  readonly airborneAttackBindings?: readonly [Partial<Record<BufferedAction, string>>, Partial<Record<BufferedAction, string>>];
  readonly trapDefinitions?: readonly GenericTrapDefinition[];
  readonly cpuProfiles?: readonly [CpuProfile | null, CpuProfile | null];
}

export interface HurtboxProfile {
  readonly standing: LocalAabb;
  readonly crouching: LocalAabb;
  readonly airborne: LocalAabb;
}

export interface BattleStepResult {
  readonly snapshot: BattleSnapshot;
  readonly events: readonly BattleEvent[];
}

const actionTarget = (action: BufferedAction): 'Attack' | 'Special' | 'Super' | 'Jump' => {
  if (action === 'super') return 'Super';
  if (action === 'skill1' || action === 'skill2') return 'Special';
  if (action === 'jump') return 'Jump';
  return 'Attack';
};

const cloneInput = (input: InputFrame): InputFrame => createInputFrame({ ...input });

const isAttackLifecycleState = (state: CharacterState): boolean =>
  state === 'Attack' || state === 'Special' || state === 'Super' || state === 'CounterAttack';

export class BattleCore {
  private combatDiagnostics: CombatDiagnostics | null = null;

  public enableCombatDiagnostics(): void { this.combatDiagnostics = new CombatDiagnostics(); }

  public getCombatDiagnosticSnapshot() {
    return structuredClone({ logicalFrame: this.runtime.logicalFrame, battlePhase: this.runtime.battlePhase,
      hitstopFrames: this.runtime.hitstopFrames,
      players: this.runtime.characters.map((character, player) =>
        projectCombatCharacter(character, this.attackDefinitions, player, this.runtime.logicalFrame)),
      collisionGeometry: this.getDebugSnapshot(),
      ...this.combatDiagnostics?.history(),
    });
  }
  private runtime: BattleRuntimeState;
  private machines: [CharacterStateMachine, CharacterStateMachine];
  private previousInputs: [InputFrame, InputFrame] = [EMPTY_INPUT_FRAME, EMPTY_INPUT_FRAME];
  private randomSource: Mulberry32;
  private readonly initialSeed: number;
  private readonly characterIds: readonly [string, string];
  private readonly characterDefinitionIds: readonly [string, string];
  private readonly stage: StageBounds;
  private readonly spatialDefinitions: readonly [CharacterSpatialDefinition, CharacterSpatialDefinition];
  private readonly initialPositions: readonly [Vec2, Vec2];
  private readonly maxHp: readonly [number, number];
  private readonly initialHp: readonly [number, number];
  private readonly initialGauge: readonly [number, number];
  private readonly initialTimerFrames: number;
  private readonly hurtboxes: readonly [LocalAabb, LocalAabb];
  private readonly hurtboxProfiles: readonly [HurtboxProfile, HurtboxProfile] | null;
  private readonly attackDefinitions: ReadonlyMap<string, AttackDefinition>;
  private readonly attackBindings: readonly [Partial<Record<BufferedAction, string>>, Partial<Record<BufferedAction, string>>];
  private readonly airborneAttackBindings: readonly [Partial<Record<BufferedAction, string>>, Partial<Record<BufferedAction, string>>];
  private readonly trapDefinitions: ReadonlyMap<string, GenericTrapDefinition>;
  private cpuControllers: [NormalCpuController | null, NormalCpuController | null];
  private events: BattleEvent[] = [];
  private nextEventSequence = 0;
  private battleGeneration = 0;

  public constructor(config: BattleCoreConfig) {
    assertUint32(config.seed, 'seed');
    this.initialSeed = config.seed;
    this.characterIds = config.characterIds ?? ['player-1', 'player-2'];
    this.characterDefinitionIds = config.characterDefinitionIds ?? this.characterIds;
    this.stage = Object.freeze({ ...(config.stage ?? DEFAULT_STAGE) });
    assertStageBounds(this.stage);
    const configuredDefinitions = config.spatialDefinitions ?? [DEFAULT_SPATIAL_DEFINITION, DEFAULT_SPATIAL_DEFINITION];
    this.spatialDefinitions = configuredDefinitions.map((definition) => Object.freeze({
      pushBox: Object.freeze({ ...definition.pushBox }),
      movement: Object.freeze({ ...definition.movement }),
    })) as unknown as readonly [CharacterSpatialDefinition, CharacterSpatialDefinition];
    assertSpatialDefinition(this.spatialDefinitions[0], 'spatialDefinitions[0]');
    assertSpatialDefinition(this.spatialDefinitions[1], 'spatialDefinitions[1]');
    const configuredPositions = config.initialPositions ?? [
      { x: 480, y: this.stage.groundY },
      { x: 800, y: this.stage.groundY },
    ];
    assertFiniteVec2(configuredPositions[0], 'initialPositions[0]');
    assertFiniteVec2(configuredPositions[1], 'initialPositions[1]');
    this.initialPositions = Object.freeze([
      Object.freeze({ ...configuredPositions[0] }),
      Object.freeze({ ...configuredPositions[1] }),
    ]);
    this.maxHp = Object.freeze([...(config.maxHp ?? [1_000, 1_000])]) as unknown as readonly [number, number];
    for (const [player, maxHp] of this.maxHp.entries()) {
      if (!Number.isFinite(maxHp) || maxHp <= 0) throw new RangeError(`maxHp[${player}] must be finite and positive.`);
    }
    this.initialHp = Object.freeze([...(config.initialHp ?? this.maxHp)]) as unknown as readonly [number, number];
    this.initialGauge = Object.freeze([...(config.initialGauge ?? [0, 0])]) as unknown as readonly [number, number];
    for (const player of [0, 1] as const) {
      if (!Number.isFinite(this.initialHp[player]) || this.initialHp[player] < 0
        || this.initialHp[player] > this.maxHp[player]) throw new RangeError(`initialHp[${player}] is invalid.`);
      if (!Number.isFinite(this.initialGauge[player]) || this.initialGauge[player] < 0
        || this.initialGauge[player] > 100) throw new RangeError(`initialGauge[${player}] is invalid.`);
    }
    this.initialTimerFrames = config.initialTimerFrames ?? INITIAL_TIMER_FRAMES;
    assertNonNegativeInteger(this.initialTimerFrames, 'initialTimerFrames');
    const configuredHurtboxes = config.hurtboxes ?? [
      this.spatialDefinitions[0].pushBox,
      this.spatialDefinitions[1].pushBox,
    ];
    this.hurtboxes = Object.freeze(configuredHurtboxes.map((box) => Object.freeze({ ...box }))) as unknown as
      readonly [LocalAabb, LocalAabb];
    for (const [player, box] of this.hurtboxes.entries()) {
      assertLocalAabb(box, `hurtboxes[${player}]`);
    }
    this.hurtboxProfiles = config.hurtboxProfiles === undefined ? null : Object.freeze(config.hurtboxProfiles.map((profile, player) => {
      assertLocalAabb(profile.standing, `hurtboxProfiles[${player}].standing`);
      assertLocalAabb(profile.crouching, `hurtboxProfiles[${player}].crouching`);
      assertLocalAabb(profile.airborne, `hurtboxProfiles[${player}].airborne`);
      return Object.freeze({
        standing: Object.freeze({ ...profile.standing }),
        crouching: Object.freeze({ ...profile.crouching }),
        airborne: Object.freeze({ ...profile.airborne }),
      });
    })) as unknown as readonly [HurtboxProfile, HurtboxProfile];
    const attackEntries = (config.attacks ?? []).map((attack) => {
      assertAttackDefinition(attack);
      return [attack.id, cloneAttackDefinition(attack)] as const;
    });
    if (new Set(attackEntries.map(([id]) => id)).size !== attackEntries.length) {
      throw new RangeError('Attack ids must be unique.');
    }
    this.attackDefinitions = new Map(attackEntries);
    this.attackBindings = Object.freeze([
      Object.freeze({ ...(config.attackBindings?.[0] ?? {}) }),
      Object.freeze({ ...(config.attackBindings?.[1] ?? {}) }),
    ]);
    this.airborneAttackBindings = Object.freeze([
      Object.freeze({ ...(config.airborneAttackBindings?.[0] ?? {}) }),
      Object.freeze({ ...(config.airborneAttackBindings?.[1] ?? {}) }),
    ]);
    for (const bindings of this.attackBindings) {
      for (const attackId of Object.values(bindings)) {
        if (attackId !== undefined && !this.attackDefinitions.has(attackId)) throw new RangeError(`Undefined bound attack: ${attackId}`);
      }
    }
    for (const bindings of this.airborneAttackBindings) {
      for (const attackId of Object.values(bindings)) {
        if (attackId !== undefined && !this.attackDefinitions.has(attackId)) throw new RangeError(`Undefined airborne attack: ${attackId}`);
      }
    }
    const trapEntries = (config.trapDefinitions ?? []).map((trap) => {
      assertTrapDefinition(trap);
      if (!this.attackDefinitions.has(trap.attackId)) throw new RangeError(`Undefined trap attack: ${trap.attackId}`);
      return [trap.id, Object.freeze({ ...trap, hitbox: Object.freeze({ ...trap.hitbox }), offset: Object.freeze({ ...trap.offset }) })] as const;
    });
    if (new Set(trapEntries.map(([id]) => id)).size !== trapEntries.length) throw new RangeError('Trap ids must be unique.');
    this.trapDefinitions = new Map(trapEntries);
    for (const attack of this.attackDefinitions.values()) {
      for (const route of attack.cancelRoutes) {
        if (!this.attackDefinitions.has(route.targetAttackId)) {
          throw new RangeError(`Undefined cancel target attack: ${route.targetAttackId}`);
        }
      }
      for (const effect of attack.effects) {
        if (effect.responseAttackId !== undefined && !this.attackDefinitions.has(effect.responseAttackId)) {
          throw new RangeError(`Undefined counter response attack: ${effect.responseAttackId}`);
        }
        if (effect.trapDefinitionId !== undefined && !this.trapDefinitions.has(effect.trapDefinitionId)) {
          throw new RangeError(`Undefined trap effect definition: ${effect.trapDefinitionId}`);
        }
      }
    }
    const characters = this.createInitialCharacters();
    this.runtime = {
      logicalFrame: 0,
      battlePhase: 'PRE_BATTLE',
      timerFrames: this.initialTimerFrames,
      hitstopFrames: 0,
      characters,
      traps: [],
      combatTraps: [],
      combos: [],
      roundResult: null,
      nextAttackInstanceSequence: 0,
      nextComboSequence: 0,
      nextEffectSequence: 0,
      nextTrapSequence: 0,
      nextCinematicSequence: 0,
      pendingCinematics: [],
      activeCinematicId: null,
      transientRequests: [],
    };
    this.machines = [new CharacterStateMachine(characters[0]), new CharacterStateMachine(characters[1])];
    this.randomSource = new Mulberry32(config.seed);
    this.cpuControllers = [
      config.cpuProfiles?.[0] === null || config.cpuProfiles?.[0] === undefined
        ? null : new NormalCpuController(config.cpuProfiles[0]),
      config.cpuProfiles?.[1] === null || config.cpuProfiles?.[1] === undefined
        ? null : new NormalCpuController(config.cpuProfiles[1]),
    ];
  }

  public get logicalFrame(): number {
    return this.runtime.logicalFrame;
  }

  public get battlePhase(): BattlePhase {
    return this.runtime.battlePhase;
  }

  public getSnapshot(): BattleSnapshot {
    return projectBattleSnapshot(this.runtime);
  }

  public getDebugSnapshot(): DebugSnapshot {
    const boxes: DebugBox[] = [];
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      boxes.push(Object.freeze({
        type: 'PUSH',
        box: Object.freeze({ ...toWorldAabb(this.spatialDefinitions[player].pushBox, character.position, character.facing) }),
        label: `P${player + 1}`,
      }));
      boxes.push(Object.freeze({
        type: 'HURT',
        box: Object.freeze({ ...toWorldAabb(this.getHurtbox(player), character.position, character.facing) }),
        label: `P${player + 1}`,
      }));
      const instance = character.currentAttack;
      const definition = instance === null ? undefined : this.attackDefinitions.get(instance.attackId);
      if (instance !== null && definition !== undefined) {
        for (const hitbox of projectActiveAttackHitboxes(definition, instance, character.position)) {
          boxes.push(Object.freeze({ type: 'HIT', box: Object.freeze({ ...hitbox.box }), label: hitbox.hitId }));
        }
      }
    }
    for (const trap of this.runtime.combatTraps) {
      boxes.push(Object.freeze({
        type: 'TRAP',
        box: Object.freeze({ ...toWorldAabb(trap.hitbox, trap.position, trap.spawnFacing) }),
        label: trap.definitionId,
      }));
    }
    return Object.freeze({
      boxes: Object.freeze(boxes),
      origins: Object.freeze(this.runtime.characters.map((character) => Object.freeze({ ...character.position }))),
    });
  }

  public getRuntimeClone(): BattleRuntimeState {
    return cloneBattleRuntimeState(this.runtime);
  }

  public getCpuRuntimeClone(player: PlayerIndex): CpuRuntimeState | null {
    this.assertPlayerIndex(player);
    return this.cpuControllers[player]?.getRuntimeClone() ?? null;
  }

  public nextRandom(): number {
    return this.randomSource.next();
  }

  public transitionBattlePhase(next: BattlePhase): boolean {
    if (!isBattlePhase(next)) throw new RangeError(`Unknown battle phase: ${String(next)}`);
    const previous = this.runtime.battlePhase;
    if (previous === next) return false;
    if (!canTransitionBattlePhase(previous, next)) {
      throw new Error(`Illegal battle phase transition: ${previous} -> ${next}`);
    }
    this.runtime.battlePhase = next;
    this.emit(BattleEventType.BATTLE_PHASE_CHANGED, { from: previous, to: next });
    if ((previous === 'FIGHTING' || previous === 'SUPER_CINEMATIC') && next === 'ROUND_END') this.cleanupTransientState();
    return true;
  }

  public completeCinematic(cinematicId: string): void {
    if (this.runtime.battlePhase !== 'SUPER_CINEMATIC' || this.runtime.activeCinematicId !== cinematicId) return;
    this.runtime.pendingCinematics.shift();
    const next = this.runtime.pendingCinematics[0];
    if (next !== undefined) {
      this.runtime.activeCinematicId = next.cinematicId;
      this.emitCinematicStarted(next);
      return;
    }
    this.runtime.activeCinematicId = null;
    this.transitionBattlePhase('FIGHTING');
  }

  public requestTransition(player: PlayerIndex, request: TransitionRequest): void {
    this.assertPlayerIndex(player);
    this.machines[player].request(request);
  }

  public requestHitstop(frames: number): void {
    assertNonNegativeInteger(frames, 'hitstopFrames');
    this.runtime.hitstopFrames = frames;
  }

  public requestKnockback(player: PlayerIndex, knockback: Vec2): void {
    this.assertPlayerIndex(player);
    assertFiniteVec2(knockback, 'knockback');
    this.runtime.characters[player].pendingKnockback = { ...knockback };
  }

  public spawnTrap(
    instanceId: string,
    ownerId: string,
    position: Vec2,
    spawnFacing: 'LEFT' | 'RIGHT',
  ): TrapSpatialState {
    const trap = createTrapSpatialState(instanceId, ownerId, position, spawnFacing, this.stage);
    this.runtime.traps.push(trap);
    return { ...trap, position: { ...trap.position } };
  }

  public step(inputs: readonly [InputFrame, InputFrame] = [EMPTY_INPUT_FRAME, EMPTY_INPUT_FRAME]): BattleStepResult {
    if (!Array.isArray(inputs) || inputs.length !== 2) throw new TypeError('BattleCore.step requires exactly two InputFrames.');
    assertInputFrame(inputs[0], 'inputs[0]');
    assertInputFrame(inputs[1], 'inputs[1]');
    this.runtime.logicalFrame += 1;
    const eventStart = this.events.length;
    const fighting = this.runtime.battlePhase === 'FIGHTING';
    const startOfStepHitstop = this.runtime.hitstopFrames;
    const effectiveInputs: [InputFrame, InputFrame] = [inputs[0], inputs[1]];
    if (fighting && startOfStepHitstop === 0) {
      for (const player of [0, 1] as const) {
        if (this.cpuControllers[player] !== null) effectiveInputs[player] = this.createCpuInput(player);
      }
    }

    for (const player of [0, 1] as const) {
      if (fighting) {
        this.runtime.characters[player].inputBuffer.beginFrame(effectiveInputs[player], this.previousInputs[player]);
        this.previousInputs[player] = this.cpuControllers[player] === null
          ? cloneInput(effectiveInputs[player])
          : createInputFrame({
            left: effectiveInputs[player].left,
            right: effectiveInputs[player].right,
            down: effectiveInputs[player].down,
            guard: effectiveInputs[player].guard,
          });
      } else if (this.runtime.battlePhase === 'SUPER_CINEMATIC') {
        this.previousInputs[player] = this.cpuControllers[player] === null ? cloneInput(inputs[player]) : EMPTY_INPUT_FRAME;
      }
    }

    if (fighting && startOfStepHitstop === 0) {
      if (this.attackDefinitions.size === 0) this.stepLegacy(effectiveInputs);
      else this.stepCombat(effectiveInputs);
    }

    if (fighting) {
      for (const character of this.runtime.characters) character.inputBuffer.endFrame();
    }
    if (fighting && startOfStepHitstop > 0) this.runtime.hitstopFrames = Math.max(0, startOfStepHitstop - 1);

    return Object.freeze({
      snapshot: this.getSnapshot(),
      events: Object.freeze(this.events.slice(eventStart)),
    });
  }

  public cleanupTransientState(): void {
    this.runtime.hitstopFrames = 0;
    this.runtime.transientRequests = [];
    this.previousInputs = [EMPTY_INPUT_FRAME, EMPTY_INPUT_FRAME];
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      character.inputBuffer.clear();
      character.currentAttack = null;
      character.combatEffects.clear();
      character.stunFramesRemaining = 0;
      character.knockdownFramesRemaining = 0;
      character.wakeFramesRemaining = 0;
      character.postWakeInvincibilityFrames = 0;
      character.activeEffects.clear();
      character.transientFlags.clear();
      character.isAirborneInvincible = false;
      character.landingRecoveryFramesRemaining = 0;
      character.landingRecoveryAppliedLogicalFrame = -1;
      character.pendingKnockback = null;
      this.machines[player].clearPending();
    }
    this.runtime.combatTraps = [];
    this.runtime.combos = [];
    this.runtime.traps = [];
    this.runtime.pendingCinematics = [];
    this.runtime.activeCinematicId = null;
    for (const controller of this.cpuControllers) controller?.reset();
  }

  public reset(): void {
    this.battleGeneration += 1;
    const characters = this.createInitialCharacters();
    this.runtime = {
      logicalFrame: 0,
      battlePhase: 'PRE_BATTLE',
      timerFrames: this.initialTimerFrames,
      hitstopFrames: 0,
      characters,
      traps: [],
      combatTraps: [],
      combos: [],
      roundResult: null,
      nextAttackInstanceSequence: 0,
      nextComboSequence: 0,
      nextEffectSequence: 0,
      nextTrapSequence: 0,
      nextCinematicSequence: 0,
      pendingCinematics: [],
      activeCinematicId: null,
      transientRequests: [],
    };
    this.machines = [new CharacterStateMachine(characters[0]), new CharacterStateMachine(characters[1])];
    this.previousInputs = [EMPTY_INPUT_FRAME, EMPTY_INPUT_FRAME];
    this.randomSource = new Mulberry32(this.initialSeed);
    for (const controller of this.cpuControllers) controller?.reset();
    this.events = [];
    this.nextEventSequence = 0;
    this.emit(BattleEventType.BATTLE_RESET, {});
  }

  public rematch(): void {
    this.reset();
  }

  public dispose(): void {
    this.cleanupTransientState();
    if (this.runtime.battlePhase !== 'FINISHED') this.transitionBattlePhase('FINISHED');
  }

  public clone(): BattleCore {
    const clone = new BattleCore({
      seed: this.initialSeed,
      characterIds: this.characterIds,
      characterDefinitionIds: this.characterDefinitionIds,
      stage: this.stage,
      spatialDefinitions: this.spatialDefinitions,
      initialPositions: this.initialPositions,
      maxHp: this.maxHp,
      initialHp: this.initialHp,
      initialGauge: this.initialGauge,
      initialTimerFrames: this.initialTimerFrames,
      hurtboxes: this.hurtboxes,
      ...(this.hurtboxProfiles === null ? {} : { hurtboxProfiles: this.hurtboxProfiles }),
      attacks: [...this.attackDefinitions.values()],
      attackBindings: this.attackBindings,
      airborneAttackBindings: this.airborneAttackBindings,
      trapDefinitions: [...this.trapDefinitions.values()],
      cpuProfiles: [this.cpuControllers[0]?.profile ?? null, this.cpuControllers[1]?.profile ?? null],
    });
    clone.runtime = cloneBattleRuntimeState(this.runtime);
    clone.machines = [
      this.machines[0].cloneFor(clone.runtime.characters[0]),
      this.machines[1].cloneFor(clone.runtime.characters[1]),
    ];
    clone.previousInputs = [cloneInput(this.previousInputs[0]), cloneInput(this.previousInputs[1])];
    clone.randomSource = this.randomSource.clone();
    clone.cpuControllers = [this.cpuControllers[0]?.clone() ?? null, this.cpuControllers[1]?.clone() ?? null];
    clone.events = this.events.map((event) => createBattleEvent(
      event.logicalFrame, event.sequence, event.type, event.payload,
    ));
    clone.nextEventSequence = this.nextEventSequence;
    clone.battleGeneration = this.battleGeneration;
    return clone;
  }

  public drainEvents(): readonly BattleEvent[] {
    const drained = Object.freeze([...this.events]);
    this.events = [];
    return drained;
  }

  private stepLegacy(inputs: readonly [InputFrame, InputFrame]): void {
    const resolvedInputs: [ResolvedInput, ResolvedInput] = [
      this.resolvePlayerInput(0, inputs[0]),
      this.resolvePlayerInput(1, inputs[1]),
    ];
    this.progressSpatialState(resolvedInputs);
    this.resolveStateMachines();
    this.runtime.timerFrames = Math.max(0, this.runtime.timerFrames - 1);
  }

  private stepCombat(inputs: readonly [InputFrame, InputFrame]): void {
    const defenseFacingsAtFrameStart = [
      this.runtime.characters[0].facing,
      this.runtime.characters[1].facing,
    ] as const;
    const resolvedInputs: [ResolvedInput, ResolvedInput] = [
      resolveInput(inputs[0], this.runtime.characters[0].inputBuffer),
      resolveInput(inputs[1], this.runtime.characters[1].inputBuffer),
    ];
    const jumpStarts: [boolean, boolean] = [false, false];
    const superEligibleAtStepTwo: [boolean, boolean] = [
      this.runtime.characters[0].gauge >= 100,
      this.runtime.characters[1].gauge >= 100,
    ];

    for (const player of [0, 1] as const) {
      jumpStarts[player] = this.processCombatInput(player, resolvedInputs[player], superEligibleAtStepTwo[player]);
    }
    if (this.activatePendingCinematics()) {
      this.resolveStateMachines();
      return;
    }
    this.applyScheduledAttackMovement();
    this.progressSpatialState(resolvedInputs, jumpStarts);
    const projected = this.progressAttacksAndProjectHitboxes();
    const pendingHits = this.collectPendingHits(projected);
    if (this.combatDiagnostics !== null && (projected.length > 0 || this.runtime.combatTraps.length > 0)) {
      this.combatDiagnostics.contact({ logicalFrame: this.runtime.logicalFrame, projectedHitboxes: projected,
        pendingHits, collisionGeometry: this.getDebugSnapshot(),
        players: this.runtime.characters.map((character, player) =>
          projectCombatCharacter(character, this.attackDefinitions, player, this.runtime.logicalFrame)),
      });
    }
    const results = Object.freeze(sortPendingHitsCanonically(pendingHits).map((pending) =>
      this.classifyCombatCandidate(
        pending,
        inputs[pending.defender],
        defenseFacingsAtFrameStart[pending.defender],
      )));
    const application = this.applyCombatResults(results);
    if (application.maxHitstop > 0) this.runtime.hitstopFrames = application.maxHitstop;
    for (const transition of application.transitions) this.machines[transition.player].request(transition.request);
    for (const response of application.counterResponses) {
      this.tryStartAttack(response.player, response.attackId, true, superEligibleAtStepTwo[response.player]);
    }
    for (const player of [0, 1] as const) {
      const buffered = resolvedInputs[player].buffered;
      if (buffered !== null && this.runtime.characters[player].inputBuffer.getCandidates()
        .some((entry) => entry.sequence === buffered.sequence)) {
        const attackId = this.attackIdForInput(player, buffered.action);
        if (attackId !== undefined) {
          const outcome = this.tryStartAttack(player, attackId, false, superEligibleAtStepTwo[player]);
          if (outcome !== 'RETAIN') this.runtime.characters[player].inputBuffer.consume(buffered.sequence);
        }
      }
    }
    for (const interrupted of application.interruptedAttacks) {
      const character = this.runtime.characters[interrupted.player];
      if (character.currentAttack?.attackInstanceId === interrupted.attackInstanceId) {
        character.currentAttack = null;
      }
    }
    const cinematicStarted = this.activatePendingCinematics();
    this.finalizeComboEvents(application.startedCombos, application.updatedCombos);
    if (cinematicStarted) {
      this.resolveStateMachines();
      return;
    }
    this.terminateFinishedCombos();
    this.decrementEffectsAndTraps();
    this.progressStunAndWakeLifecycle();
    this.runtime.timerFrames = Math.max(0, this.runtime.timerFrames - 1);
    const roundEnded = this.resolveRound();
    this.resolveStateMachines();
    if (roundEnded) this.cleanupTransientState();
  }

  private processCombatInput(player: PlayerIndex, resolved: ResolvedInput, superEligible: boolean): boolean {
    const character = this.runtime.characters[player];
    const heldTarget = resolved.held.guard
      ? 'Guard'
      : resolved.held.crouch
        ? 'Crouch'
        : resolved.held.horizontal === 0
          ? 'Idle'
          : 'Walk';
    if (this.canMove(character.state)) {
      this.machines[player].request({ target: heldTarget, reason: TransitionReason.INPUT_HELD });
    }
    if (resolved.buffered === null) return false;
    const entry = resolved.buffered;
    if (entry.action === 'jump') {
      if (this.canJump(character)) {
        character.inputBuffer.consume(entry.sequence);
        this.machines[player].request({ target: 'Jump', reason: TransitionReason.INPUT_BUFFERED });
        return true;
      }
      if (character.state === 'Jump' || (!character.isGrounded && this.acceptsPlayerControl(character.state))) {
        character.inputBuffer.consume(entry.sequence);
      }
      return false;
    }

    const attackId = this.attackIdForInput(player, entry.action);
    if (attackId === undefined) {
      if (!character.isGrounded && this.attackDefinitions.size > 0) {
        character.inputBuffer.consume(entry.sequence);
        return false;
      }
      if (this.acceptsPlayerControl(character.state)) {
        character.inputBuffer.consume(entry.sequence);
        this.machines[player].request({ target: actionTarget(entry.action), reason: TransitionReason.INPUT_BUFFERED });
      }
      return false;
    }
    const outcome = this.tryStartAttack(player, attackId, false, superEligible);
    if (outcome !== 'RETAIN') character.inputBuffer.consume(entry.sequence);
    return false;
  }

  private tryStartAttack(
    player: PlayerIndex,
    attackId: string,
    counterResponse: boolean,
    superEligible: boolean,
  ): 'STARTED' | 'DISCARD' | 'RETAIN' {
    const definition = this.attackDefinitions.get(attackId);
    if (definition === undefined) throw new Error(`Undefined attack: ${attackId}`);
    const character = this.runtime.characters[player];
    if (character.landingRecoveryFramesRemaining > 0) return 'RETAIN';
    if (character.currentAttack !== null) {
      const currentDefinition = this.attackDefinitions.get(character.currentAttack.attackId);
      const cancelAllowed = currentDefinition?.cancelRoutes.some((route) => route.targetAttackId === attackId
        && isCancelRouteEligible(
          route,
          character.currentAttack?.elapsedFrames ?? 0,
          character.currentAttack?.confirmedHit ?? false,
          character.currentAttack?.confirmedBlock ?? false,
        )) ?? false;
      if (!cancelAllowed && !counterResponse) return 'RETAIN';
    } else if (!counterResponse && !this.acceptsPlayerControl(character.state)) {
      return 'RETAIN';
    }
    if (definition.attackType === 'SUPER' && (!superEligible || character.gauge < 100)) return 'DISCARD';
    if (character.gauge < definition.gaugeCost) return 'DISCARD';
    if (definition.airOnly === true && character.isGrounded) return 'DISCARD';
    if (definition.airOnly !== true && !counterResponse && !character.isGrounded) return 'DISCARD';
    const spawnTrap = definition.effects.find((effect) => effect.type === 'SPAWN_TRAP')?.trapDefinitionId;
    const trapDefinition = spawnTrap === undefined ? undefined : this.trapDefinitions.get(spawnTrap);
    if (trapDefinition?.maxActivePerOwner !== undefined
      && this.runtime.combatTraps.filter((trap) => trap.ownerId === character.id
        && trap.definitionId === trapDefinition.id).length >= trapDefinition.maxActivePerOwner) return 'RETAIN';

    if (definition.gaugeCost > 0) {
      const previous = character.gauge;
      character.gauge = clampGauge(character.gauge - definition.gaugeCost);
      this.emit(BattleEventType.GAUGE_CHANGED, { player, from: previous, to: character.gauge, reason: 'ATTACK_COST' });
    }
    const instanceId = `attack-${this.runtime.nextAttackInstanceSequence}`;
    this.runtime.nextAttackInstanceSequence += 1;
    character.currentAttack = createAttackInstance(
      definition, instanceId, character.id, character.facing, this.runtime.logicalFrame,
    );
    const targetState = counterResponse
      ? 'CounterAttack'
      : definition.attackType === 'SUPER'
        ? 'Super'
        : definition.attackType === 'SPECIAL' || definition.attackType === 'PROJECTILE'
          ? 'Special'
          : 'Attack';
    this.machines[player].request({ target: targetState, reason: TransitionReason.ATTACK_STARTED });
    if (targetState === 'Attack' || targetState === 'Special' || targetState === 'Super') {
      character.postWakeInvincibilityFrames = 0;
    }
    const facingSign = character.facing === 'RIGHT' ? 1 : -1;
    character.velocity.x += definition.movement.x * facingSign;
    character.velocity.y += definition.movement.y;
    if (definition.movement.y < 0) character.isGrounded = false;
    this.emit(BattleEventType.ATTACK_STARTED, {
      player, characterId: character.id, attackId, attackInstanceId: instanceId,
    });
    if (definition.attackType === 'SUPER' && definition.cinematicId !== null) this.enqueueCinematic(player, instanceId);
    this.applyTriggeredEffects(definition, 'ON_START', player, player, null);
    return 'STARTED';
  }

  private progressAttacksAndProjectHitboxes(): readonly (ProjectedAttackHitbox & { readonly player: PlayerIndex })[] {
    const projected: (ProjectedAttackHitbox & { readonly player: PlayerIndex })[] = [];
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      const instance = character.currentAttack;
      if (instance === null) continue;
      const definition = this.attackDefinitions.get(instance.attackId);
      if (definition === undefined) throw new Error(`Undefined runtime attack: ${instance.attackId}`);
      if (instance.justStarted) instance.justStarted = false;
      else instance.elapsedFrames += 1;
      if (instance.elapsedFrames >= getAttackTotalFrames(definition)) {
        character.currentAttack = null;
        if (isAttackLifecycleState(character.state)) {
          this.machines[player].request({
            target: definition.airOnly === true && !character.isGrounded ? 'Jump' : 'Idle',
            reason: TransitionReason.STUN_COMPLETE,
          });
        }
        continue;
      }
      this.applyTriggeredEffects(definition, 'ON_FRAME', player, player, null, instance.elapsedFrames);
      for (const hitbox of projectActiveAttackHitboxes(definition, instance, character.position)) {
        projected.push(Object.freeze({ ...hitbox, player }));
      }
    }
    return Object.freeze(projected);
  }

  private collectPendingHits(
    projected: readonly (ProjectedAttackHitbox & { readonly player: PlayerIndex })[],
  ): PendingHit[] {
    const pending: PendingHit[] = [];
    for (const hitbox of projected) {
      const defender = hitbox.player === 0 ? 1 : 0;
      const defenderState = this.runtime.characters[defender];
      const key = makeHitRecordKey(hitbox.attackInstanceId, hitbox.hitId, defenderState.id);
      const instance = this.runtime.characters[hitbox.player].currentAttack;
      if (instance === null || instance.hitRecord.has(key)) continue;
      const hurtbox = toWorldAabb(this.getHurtbox(defender), defenderState.position, defenderState.facing);
      const overlap = getAabbOverlap(hitbox.box, hurtbox);
      if (overlap.x <= 0 || overlap.y <= 0) continue;
      pending.push(Object.freeze({
        logicalFrame: this.runtime.logicalFrame,
        attacker: hitbox.player,
        defender,
        attackerId: this.runtime.characters[hitbox.player].id,
        defenderId: defenderState.id,
        attackInstanceId: hitbox.attackInstanceId,
        attackId: hitbox.attackId,
        hitId: hitbox.hitId,
        source: 'ATTACK',
        sourceInstanceId: hitbox.attackInstanceId,
        attackFacing: instance.startFacing,
      }));
    }
    for (const trap of this.runtime.combatTraps) {
      const owner = this.playerForCharacterId(trap.ownerId);
      const defender = owner === 0 ? 1 : 0;
      const defenderState = this.runtime.characters[defender];
      const hitId = 'trap-contact';
      const key = makeHitRecordKey(trap.instanceId, hitId, defenderState.id);
      if (trap.hitRecord.has(key)) continue;
      const trapBox = toWorldAabb(trap.hitbox, trap.position, trap.spawnFacing);
      const hurtbox = toWorldAabb(this.getHurtbox(defender), defenderState.position, defenderState.facing);
      const overlap = getAabbOverlap(trapBox, hurtbox);
      if (overlap.x <= 0 || overlap.y <= 0) continue;
      pending.push(Object.freeze({
        logicalFrame: this.runtime.logicalFrame,
        attacker: owner,
        defender,
        attackerId: trap.ownerId,
        defenderId: defenderState.id,
        attackInstanceId: trap.instanceId,
        attackId: trap.attackId,
        hitId,
        source: 'TRAP',
        sourceInstanceId: trap.instanceId,
        attackFacing: trap.spawnFacing,
      }));
    }
    return pending;
  }

  private classifyCombatCandidate(
    pending: PendingHit,
    input: InputFrame,
    defenseFacingAtFrameStart: CharacterRuntimeState['facing'],
  ): CombatResult {
    const attack = this.attackDefinitions.get(pending.attackId);
    if (attack === undefined) throw new Error(`Undefined pending attack: ${pending.attackId}`);
    const defender = this.runtime.characters[pending.defender];
    const currentDefinition = defender.currentAttack === null
      ? undefined
      : this.attackDefinitions.get(defender.currentAttack.attackId);
    const attackInvincible = currentDefinition !== undefined && defender.currentAttack !== null
      && isAttackInvulnerable(currentDefinition, defender.currentAttack.elapsedFrames);
    const result = classifyPendingHit(pending, resolveAttackForHit(attack, pending.hitId), {
      state: defender.state,
      facing: defenseFacingAtFrameStart,
      input,
      invincible: defender.postWakeInvincibilityFrames > 0
        || defender.isAirborneInvincible
        || defender.state === 'KO'
        || attackInvincible,
      counterWindowActive: [...defender.combatEffects.values()]
        .some((effect) => effect.type === 'COUNTER_WINDOW' && effect.remainingFrames > 0),
    });
    if (this.combatDiagnostics !== null) this.combatDiagnostics.record(result,
      projectCombatCharacter(defender, this.attackDefinitions, pending.defender, this.runtime.logicalFrame));
    return result;
  }

  private applyCombatResults(results: readonly CombatResult[]): {
    maxHitstop: number;
    transitions: { player: PlayerIndex; request: TransitionRequest }[];
    counterResponses: { player: PlayerIndex; attackId: string }[];
    interruptedAttacks: { player: PlayerIndex; attackInstanceId: string }[];
    startedCombos: Set<string>;
    updatedCombos: Set<string>;
  } {
    let maxHitstop = 0;
    const transitions: { player: PlayerIndex; request: TransitionRequest }[] = [];
    const counterResponses: { player: PlayerIndex; attackId: string }[] = [];
    const interruptedAttacks: { player: PlayerIndex; attackInstanceId: string }[] = [];
    const startedCombos = new Set<string>();
    const updatedCombos = new Set<string>();
    const gaugeDeltas: [number, number] = [0, 0];
    const consumedTraps = new Set<string>();

    for (const result of results) {
      this.combatDiagnostics?.application(result, this.runtime.characters[result.defender].hp,
        this.runtime.characters[result.defender].hp);
      if (result.result === 'IGNORED_INVINCIBLE') continue;
      const attacker = this.runtime.characters[result.attacker];
      const defender = this.runtime.characters[result.defender];
      const recordKey = makeHitRecordKey(result.attackInstanceId, result.hitId, result.defenderId);
      if (result.source === 'ATTACK' && attacker.currentAttack?.attackInstanceId === result.attackInstanceId) {
        attacker.currentAttack.hitRecord.add(recordKey);
      } else if (result.source === 'TRAP') {
        this.runtime.combatTraps.find((trap) => trap.instanceId === result.sourceInstanceId)?.hitRecord.add(recordKey);
        consumedTraps.add(result.sourceInstanceId);
      }

      if (result.result === 'COUNTERED') {
        if (result.source === 'ATTACK' && attacker.currentAttack?.attackInstanceId === result.attackInstanceId) {
          attacker.currentAttack = null;
        }
        transitions.push({
          player: result.attacker,
          request: { target: 'Hitstun', reason: TransitionReason.COMBAT_COUNTER },
        });
        attacker.stunFramesRemaining = Math.max(1, result.attack.hitstunFrames);
        attacker.landingRecoveryFramesRemaining = 0;
        attacker.stunAppliedLogicalFrame = this.runtime.logicalFrame;
        const responseAttackId = this.getCounterResponseAttackId(defender);
        if (responseAttackId !== null) counterResponses.push({ player: result.defender, attackId: responseAttackId });
        this.emit(BattleEventType.COUNTER, {
          attacker: result.attacker, defender: result.defender, attackId: result.attackId, hitId: result.hitId,
        });
        this.applyCounterEffects(result.defender, result.attacker);
        continue;
      }

      let appliedDamage = result.attack.chipDamage;
      let combo: ComboRuntime | null = null;
      if (result.result === 'HIT') {
        const comboResolution = this.getOrCreateCombo(result.attackerId, result.defenderId);
        combo = comboResolution.combo;
        if (comboResolution.created) startedCombos.add(combo.comboId);
        appliedDamage = calculateScaledDamage(result.attack.damage, combo.hitCount);
      }
      const previousHp = defender.hp;
      defender.hp = Math.max(0, Math.min(defender.maxHp, defender.hp - appliedDamage));
      this.combatDiagnostics?.application(result, previousHp, defender.hp);
      if (appliedDamage > 0) {
        this.emit(BattleEventType.DAMAGE_APPLIED, {
          attacker: result.attacker, defender: result.defender, amount: appliedDamage, from: previousHp, to: defender.hp,
        });
      }
      maxHitstop = Math.max(maxHitstop, result.hitstopFrames);

      if (result.result === 'GUARDED') {
        if (attacker.currentAttack?.attackInstanceId === result.attackInstanceId) attacker.currentAttack.confirmedBlock = true;
        gaugeDeltas[result.attacker] += result.attack.gaugeGain.attackerOnBlock;
        gaugeDeltas[result.defender] += result.attack.gaugeGain.defenderOnBlock;
        transitions.push({
          player: result.defender,
          request: { target: 'Guard', reason: TransitionReason.COMBAT_GUARD },
        });
        defender.stunFramesRemaining = result.attack.blockstunFrames;
        defender.stunAppliedLogicalFrame = this.runtime.logicalFrame;
        this.emit(BattleEventType.GUARD, {
          attacker: result.attacker, defender: result.defender, attackId: result.attackId, hitId: result.hitId,
        });
        this.applyTriggeredEffects(result.attack, 'ON_BLOCK', result.attacker, result.defender, null);
      } else {
        if (attacker.currentAttack?.attackInstanceId === result.attackInstanceId) attacker.currentAttack.confirmedHit = true;
        if (defender.currentAttack !== null) {
          interruptedAttacks.push({
            player: result.defender,
            attackInstanceId: defender.currentAttack.attackInstanceId,
          });
        }
        defender.landingRecoveryFramesRemaining = 0;
        gaugeDeltas[result.attacker] += result.attack.gaugeGain.attackerOnHit;
        gaugeDeltas[result.defender] += result.attack.gaugeGain.defenderOnHit;
        if (combo !== null) {
          combo.hitCount += 1;
          combo.totalDamage += appliedDamage;
          updatedCombos.add(combo.comboId);
        }
        this.emit(BattleEventType.HIT, {
          attacker: result.attacker, defender: result.defender, attackId: result.attackId, hitId: result.hitId,
        });
        this.applyTriggeredEffects(result.attack, 'ON_HIT', result.attacker, result.defender, combo);
        defender.pendingKnockback = {
          x: result.attack.knockback.x * (result.attackFacing === 'RIGHT' ? 1 : -1),
          y: result.attack.knockback.y,
        };
        if (!defender.isGrounded) defender.isAirborneInvincible = true;
        if (result.attack.causesKnockdown && defender.isGrounded) {
          defender.knockdownFramesRemaining = 60;
          defender.wakeFramesRemaining = 0;
          defender.lifecycleAppliedLogicalFrame = this.runtime.logicalFrame;
          transitions.push({
            player: result.defender,
            request: { target: 'Knockdown', reason: TransitionReason.COMBAT_HIT },
          });
          this.emit(BattleEventType.KNOCKDOWN, { player: result.defender, reason: 'ATTACK' });
        } else {
          defender.stunFramesRemaining = result.attack.hitstunFrames;
          defender.stunAppliedLogicalFrame = this.runtime.logicalFrame;
          transitions.push({
            player: result.defender,
            request: { target: 'Hitstun', reason: TransitionReason.COMBAT_HIT },
          });
        }
      }
    }

    this.runtime.combatTraps = this.runtime.combatTraps.filter((trap) => !consumedTraps.has(trap.instanceId));
    for (const player of [0, 1] as const) {
      if (gaugeDeltas[player] === 0) continue;
      const character = this.runtime.characters[player];
      const previous = character.gauge;
      character.gauge = clampGauge(character.gauge + gaugeDeltas[player]);
      if (character.gauge !== previous) {
        this.emit(BattleEventType.GAUGE_CHANGED, {
          player, from: previous, to: character.gauge, reason: 'COMBAT_RESULT',
        });
      }
    }
    return { maxHitstop, transitions, counterResponses, interruptedAttacks, startedCombos, updatedCombos };
  }

  private getOrCreateCombo(attackerId: string, defenderId: string): { combo: ComboRuntime; created: boolean } {
    const existing = this.runtime.combos.find((combo) => combo.attackerId === attackerId && combo.defenderId === defenderId);
    if (existing !== undefined) return { combo: existing, created: false };
    const combo = createComboRuntime(`combo-${this.runtime.nextComboSequence}`, attackerId, defenderId);
    this.runtime.nextComboSequence += 1;
    this.runtime.combos.push(combo);
    return { combo, created: true };
  }

  private finalizeComboEvents(started: ReadonlySet<string>, updated: ReadonlySet<string>): void {
    for (const combo of [...this.runtime.combos].sort((left, right) => left.comboId.localeCompare(right.comboId))) {
      if (started.has(combo.comboId)) {
        this.emit(BattleEventType.COMBO_STARTED, {
          comboId: combo.comboId, attackerId: combo.attackerId, defenderId: combo.defenderId,
        });
      }
      if (updated.has(combo.comboId)) {
        this.emit(BattleEventType.COMBO_UPDATED, {
          comboId: combo.comboId, hitCount: combo.hitCount, totalDamage: combo.totalDamage,
        });
      }
    }
  }

  private terminateFinishedCombos(): void {
    const retained: ComboRuntime[] = [];
    for (const combo of this.runtime.combos) {
      const defender = this.runtime.characters[this.playerForCharacterId(combo.defenderId)];
      const actionable = this.acceptsPlayerControl(defender.state)
        && defender.stunFramesRemaining === 0
        && defender.knockdownFramesRemaining === 0
        && defender.wakeFramesRemaining === 0;
      if (actionable) {
        this.emit(BattleEventType.COMBO_ENDED, {
          comboId: combo.comboId, hitCount: combo.hitCount, totalDamage: combo.totalDamage,
        });
      } else retained.push(combo);
    }
    this.runtime.combos = retained;
  }

  private applyTriggeredEffects(
    attack: AttackDefinition,
    trigger: EffectTrigger,
    source: PlayerIndex,
    target: PlayerIndex,
    combo: ComboRuntime | null,
    elapsedFrame?: number,
  ): void {
    for (const effect of attack.effects) {
      if (effect.trigger !== trigger || (trigger === 'ON_FRAME' && effect.frame !== undefined && effect.frame !== elapsedFrame)) continue;
      if (effect.minimumTimerFrames !== undefined && this.runtime.timerFrames < effect.minimumTimerFrames) continue;
      if (effect.oncePerCombo && combo !== null) {
        const key = makeOncePerComboKey(combo.comboId, effect.id);
        if (combo.oncePerComboAppliedEffects.has(key)) continue;
        combo.oncePerComboAppliedEffects.add(key);
      }
      this.applyEffect(effect, source, target);
    }
  }

  private applyCounterEffects(source: PlayerIndex, target: PlayerIndex): void {
    const instance = this.runtime.characters[source].currentAttack;
    if (instance === null) return;
    const definition = this.attackDefinitions.get(instance.attackId);
    if (definition !== undefined) this.applyTriggeredEffects(definition, 'ON_COUNTER', source, target, null);
  }

  private applyEffect(effect: EffectDefinition, source: PlayerIndex, target: PlayerIndex): void {
    if (effect.type === 'TIME_PENALTY') {
      if (effect.minimumTimerFrames !== undefined && this.runtime.timerFrames < effect.minimumTimerFrames) return;
      this.runtime.timerFrames = Math.max(0, this.runtime.timerFrames - Math.max(0, effect.value ?? 0));
    } else if (effect.type === 'SPAWN_TRAP') {
      const trapId = effect.trapDefinitionId;
      const definition = trapId === undefined ? undefined : this.trapDefinitions.get(trapId);
      if (definition === undefined) throw new Error(`Undefined trap effect definition: ${String(trapId)}`);
      const owner = this.runtime.characters[source];
      if (definition.maxActivePerOwner !== undefined
        && this.runtime.combatTraps.filter((trap) => trap.ownerId === owner.id
          && trap.definitionId === definition.id).length >= definition.maxActivePerOwner) return;
      const facingSign = owner.facing === 'RIGHT' ? 1 : -1;
      const spatial = createTrapSpatialState(
        `combat-trap-${this.runtime.nextTrapSequence}`,
        owner.id,
        { x: owner.position.x + definition.offset.x * facingSign, y: owner.position.y + definition.offset.y },
        owner.facing,
        this.stage,
      );
      this.runtime.nextTrapSequence += 1;
      this.runtime.combatTraps.push(createCombatTrapRuntime(
        spatial.instanceId, spatial.ownerId, spatial.position, spatial.spawnFacing, definition, this.runtime.logicalFrame,
      ));
    } else {
      const recipient = effect.type === 'COUNTER_WINDOW' ? this.runtime.characters[source] : this.runtime.characters[target];
      const runtimeId = `effect-${this.runtime.nextEffectSequence}`;
      this.runtime.nextEffectSequence += 1;
      const runtimeEffect: CombatEffectRuntime = {
        runtimeId,
        definitionId: effect.id,
        type: effect.type,
        remainingFrames: effect.durationFrames ?? 0,
        strength: effect.strength ?? 1,
        responseAttackId: effect.responseAttackId ?? null,
        appliedLogicalFrame: this.runtime.logicalFrame,
      };
      recipient.combatEffects.set(runtimeId, runtimeEffect);
    }
    this.emit(BattleEventType.EFFECT_APPLIED, {
      effectId: effect.id, effectType: effect.type, source, target,
    });
  }

  private decrementEffectsAndTraps(): void {
    for (const player of [0, 1] as const) {
      const effects = this.runtime.characters[player].combatEffects;
      for (const [key, effect] of effects) {
        if (effect.appliedLogicalFrame === this.runtime.logicalFrame) continue;
        effect.remainingFrames -= 1;
        if (effect.remainingFrames <= 0) {
          effects.delete(key);
          this.emit(BattleEventType.EFFECT_EXPIRED, {
            player, effectId: effect.definitionId, effectType: effect.type,
          });
        }
      }
    }
    const retained = [];
    for (const trap of this.runtime.combatTraps) {
      if (trap.spawnedLogicalFrame !== this.runtime.logicalFrame) trap.remainingFrames -= 1;
      if (trap.remainingFrames > 0) retained.push(trap);
      else this.emit(BattleEventType.EFFECT_EXPIRED, { effectId: trap.definitionId, effectType: 'SPAWN_TRAP' });
    }
    this.runtime.combatTraps = retained;
  }

  private progressStunAndWakeLifecycle(): void {
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      const canLeaveStun = character.state === 'Guard'
        || (character.state === 'Hitstun' && character.isGrounded);
      if (character.stunFramesRemaining === 0 && canLeaveStun
        && character.stunAppliedLogicalFrame < this.runtime.logicalFrame) {
        this.machines[player].request({ target: 'Idle', reason: TransitionReason.STUN_COMPLETE });
      }
      if (character.stunFramesRemaining > 0 && character.stunAppliedLogicalFrame !== this.runtime.logicalFrame) {
        character.stunFramesRemaining -= 1;
        if (character.stunFramesRemaining === 0 && canLeaveStun) {
          this.machines[player].request({ target: 'Idle', reason: TransitionReason.STUN_COMPLETE });
        }
      }
      if (character.knockdownFramesRemaining > 0
        && character.lifecycleAppliedLogicalFrame !== this.runtime.logicalFrame) {
        character.knockdownFramesRemaining -= 1;
        if (character.knockdownFramesRemaining === 0) {
          character.wakeFramesRemaining = 20;
          character.lifecycleAppliedLogicalFrame = this.runtime.logicalFrame;
          this.emit(BattleEventType.WAKE_UP, { player, stage: 'WAKE_STARTED' });
        }
      } else if (character.wakeFramesRemaining > 0
        && character.lifecycleAppliedLogicalFrame !== this.runtime.logicalFrame) {
        character.wakeFramesRemaining -= 1;
        if (character.wakeFramesRemaining === 0) {
          character.postWakeInvincibilityFrames = 30;
          character.postWakeAppliedLogicalFrame = this.runtime.logicalFrame;
          this.machines[player].request({ target: 'Idle', reason: TransitionReason.WAKE_COMPLETE });
        }
      }
      if (character.postWakeInvincibilityFrames > 0
        && character.postWakeAppliedLogicalFrame !== this.runtime.logicalFrame) {
        character.postWakeInvincibilityFrames -= 1;
      }
      if (character.landingRecoveryFramesRemaining > 0
        && character.landingRecoveryAppliedLogicalFrame !== this.runtime.logicalFrame) {
        character.landingRecoveryFramesRemaining -= 1;
        if (character.landingRecoveryFramesRemaining === 0) {
          this.machines[player].request({ target: 'Idle', reason: TransitionReason.STUN_COMPLETE });
        }
      }
    }
  }

  private getCounterResponseAttackId(character: CharacterRuntimeState): string | null {
    const effects = [...character.combatEffects.values()]
      .filter((effect) => effect.type === 'COUNTER_WINDOW' && effect.remainingFrames > 0 && effect.responseAttackId !== null)
      .sort((left, right) => left.runtimeId.localeCompare(right.runtimeId));
    return effects[0]?.responseAttackId ?? null;
  }

  private resolveRound(): boolean {
    if (this.runtime.roundResult !== null) return false;
    const result = resolveRoundResult(
      [this.runtime.characters[0].hp, this.runtime.characters[1].hp],
      this.runtime.timerFrames,
    );
    if (result === null) return false;
    this.runtime.roundResult = result;
    if (result.reason === 'DRAW') this.emit(BattleEventType.DRAW, { reason: result.reason });
    else if (result.reason === 'TIME_UP') this.emit(BattleEventType.TIME_UP, { winner: result.winner });
    else {
      const loser = result.winner === 0 ? 1 : 0;
      this.machines[loser].request({ target: 'KO', reason: TransitionReason.ROUND_KO });
      this.emit(BattleEventType.KO, { winner: result.winner, loser });
    }
    const previous = this.runtime.battlePhase;
    this.runtime.battlePhase = 'ROUND_END';
    this.emit(BattleEventType.BATTLE_PHASE_CHANGED, { from: previous, to: 'ROUND_END' });
    return true;
  }

  private resolveStateMachines(): void {
    for (const player of [0, 1] as const) {
      const transition = this.machines[player].resolve();
      if (transition !== null) {
        const character = this.runtime.characters[player];
        if (transition.target === 'Knockdown') {
          if (character.knockdownFramesRemaining === 0) {
            character.knockdownFramesRemaining = 60;
            character.lifecycleAppliedLogicalFrame = this.runtime.logicalFrame;
            this.emit(BattleEventType.KNOCKDOWN, { player, reason: transition.reason });
          }
          character.isAirborneInvincible = false;
        }
        if (transition.target === 'Attack' || transition.target === 'Special' || transition.target === 'Super') {
          character.postWakeInvincibilityFrames = 0;
        }
        this.emit(BattleEventType.CHARACTER_STATE_CHANGED, {
          player,
          characterId: character.id,
          from: transition.from,
          to: transition.target,
          reason: transition.reason,
        });
      }
      this.machines[player].finishLogicalFrame(transition !== null);
    }
  }

  private playerForCharacterId(characterId: string): PlayerIndex {
    if (this.runtime.characters[0].id === characterId) return 0;
    if (this.runtime.characters[1].id === characterId) return 1;
    throw new Error(`Unknown character id: ${characterId}`);
  }

  private resolvePlayerInput(player: PlayerIndex, input: InputFrame): ResolvedInput {
    const character = this.runtime.characters[player];
    const resolved = resolveInput(input, character.inputBuffer);
    if (resolved.buffered !== null) {
      const target = actionTarget(resolved.buffered.action);
      if (this.acceptsPlayerControl(character.state) && (target !== 'Jump' || this.canJump(character))) {
        this.machines[player].request({ target, reason: TransitionReason.INPUT_BUFFERED });
      }
      character.inputBuffer.consume(resolved.buffered.sequence);
    }
    const heldTarget = resolved.held.guard
      ? 'Guard'
      : resolved.held.crouch
        ? 'Crouch'
        : resolved.held.horizontal === 0
          ? 'Idle'
          : 'Walk';
    if (this.canMove(character.state)) {
      this.machines[player].request({ target: heldTarget, reason: TransitionReason.INPUT_HELD });
    }
    return resolved;
  }

  private progressSpatialState(
    inputs: readonly [ResolvedInput, ResolvedInput],
    jumpStarts?: readonly [boolean, boolean],
  ): void {
    for (const player of [0, 1] as const) this.updateVelocity(player, inputs[player], jumpStarts?.[player]);
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      const movement = this.spatialDefinitions[player].movement;
      if (!character.isGrounded) character.velocity.y += movement.gravity;
      character.position.x += character.velocity.x;
      character.position.y += character.velocity.y;
      if (character.position.y >= this.stage.groundY) {
        const landed = !character.isGrounded;
        character.position.y = this.stage.groundY;
        character.velocity.y = 0;
        character.isGrounded = true;
        const landedAttack = character.currentAttack === null
          ? undefined : this.attackDefinitions.get(character.currentAttack.attackId);
        if (landed && character.state === 'Hitstun') {
          if (landedAttack?.airOnly === true) character.currentAttack = null;
          character.landingRecoveryFramesRemaining = 0;
          this.machines[player].request({
            target: 'Knockdown',
            reason: TransitionReason.AIRBORNE_HITSTUN_LANDING,
          });
          character.isAirborneInvincible = false;
        } else if (landed && landedAttack?.airOnly === true) {
          character.currentAttack = null;
          character.landingRecoveryFramesRemaining = landedAttack.landingRecoveryFrames ?? 0;
          character.landingRecoveryAppliedLogicalFrame = this.runtime.logicalFrame;
          this.machines[player].request({ target: 'Attack', reason: TransitionReason.GROUND_LANDING });
        } else if (landed && character.state === 'Jump') {
          this.machines[player].request({ target: 'Idle', reason: TransitionReason.GROUND_LANDING });
        }
      } else {
        character.isGrounded = false;
      }
    }

    const bodies = this.createPushBodies();
    resolveBodyPushAndWalls(bodies, this.stage);
    updateFacing(bodies);
    this.runtime.characters[0].facing = bodies[0].facing;
    this.runtime.characters[1].facing = bodies[1].facing;
    this.assertSpatialInvariants(bodies);
  }

  private updateVelocity(player: PlayerIndex, input: ResolvedInput, jumpStarted?: boolean): void {
    const character = this.runtime.characters[player];
    const movement = this.spatialDefinitions[player].movement;
    const slowMultiplier = [...character.combatEffects.values()]
      .filter((effect) => effect.type === 'SLOW' && effect.remainingFrames > 0)
      .reduce((current, effect) => Math.min(current, effect.strength), 1);
    if (this.canMove(character.state)) {
      const horizontal = input.held.horizontal;
      if (horizontal === 0) character.velocity.x = 0;
      else if (!character.isGrounded) character.velocity.x = horizontal * movement.airControlSpeed * slowMultiplier;
      else {
        const movingForward = (horizontal > 0 && character.facing === 'RIGHT')
          || (horizontal < 0 && character.facing === 'LEFT');
        character.velocity.x = horizontal * (movingForward ? movement.forwardSpeed : movement.backwardSpeed) * slowMultiplier;
      }
      if ((jumpStarted ?? input.buffered?.action === 'jump') && character.isGrounded) {
        character.velocity.y = movement.jumpVelocity;
        character.isGrounded = false;
      }
    }
    if (character.pendingKnockback !== null) {
      character.velocity.x += character.pendingKnockback.x;
      character.velocity.y = character.pendingKnockback.y;
      if (character.pendingKnockback.y < 0) character.isGrounded = false;
      character.pendingKnockback = null;
    }
  }

  private createPushBodies(): [PushBody, PushBody] {
    return [0, 1].map((player) => ({
      player: player as PlayerIndex,
      position: this.runtime.characters[player as PlayerIndex].position,
      facing: this.runtime.characters[player as PlayerIndex].facing,
      pushBox: this.spatialDefinitions[player as PlayerIndex].pushBox,
    })) as [PushBody, PushBody];
  }

  private assertSpatialInvariants(bodies: readonly [PushBody, PushBody]): void {
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      assertFiniteVec2(character.position, `player${player + 1}.position`);
      assertFiniteVec2(character.velocity, `player${player + 1}.velocity`);
      if (character.position.y > this.stage.groundY) throw new Error('Character root is below the ground.');
      const world = toWorldAabb(bodies[player].pushBox, bodies[player].position, bodies[player].facing);
      if (world.minX < this.stage.minX || world.maxX > this.stage.maxX) {
        throw new Error('Character push box is outside the stage.');
      }
    }
    const overlap = getAabbOverlap(
      toWorldAabb(bodies[0].pushBox, bodies[0].position, bodies[0].facing),
      toWorldAabb(bodies[1].pushBox, bodies[1].position, bodies[1].facing),
    );
    if (overlap.x > 0 && overlap.y > 0) throw new Error('Character push boxes overlap after resolution.');
  }

  private createInitialCharacters(): [CharacterRuntimeState, CharacterRuntimeState] {
    const characters: [CharacterRuntimeState, CharacterRuntimeState] = [
      createCharacterRuntimeState(this.characterIds[0], 'RIGHT', this.initialPositions[0], this.characterDefinitionIds[0]),
      createCharacterRuntimeState(this.characterIds[1], 'LEFT', this.initialPositions[1], this.characterDefinitionIds[1]),
    ];
    for (const character of characters) {
      if (character.position.y >= this.stage.groundY) {
        character.position.y = this.stage.groundY;
        character.isGrounded = true;
      }
    }
    characters[0].maxHp = this.maxHp[0];
    characters[0].hp = this.initialHp[0];
    characters[0].gauge = this.initialGauge[0];
    characters[1].maxHp = this.maxHp[1];
    characters[1].hp = this.initialHp[1];
    characters[1].gauge = this.initialGauge[1];
    const bodies: [PushBody, PushBody] = [
      { player: 0, position: characters[0].position, facing: characters[0].facing, pushBox: this.spatialDefinitions[0].pushBox },
      { player: 1, position: characters[1].position, facing: characters[1].facing, pushBox: this.spatialDefinitions[1].pushBox },
    ];
    resolveBodyPushAndWalls(bodies, this.stage);
    updateFacing(bodies);
    characters[0].facing = bodies[0].facing;
    characters[1].facing = bodies[1].facing;
    return characters;
  }

  private acceptsPlayerControl(state: CharacterRuntimeState['state']): boolean {
    return state !== 'Hitstun' && state !== 'Knockdown' && state !== 'KO';
  }

  private canMove(state: CharacterRuntimeState['state']): boolean {
    return state === 'Idle' || state === 'Walk' || state === 'Crouch' || state === 'Guard' || state === 'Jump';
  }

  private canJump(character: CharacterRuntimeState): boolean {
    return character.isGrounded
      && (character.state === 'Idle' || character.state === 'Walk'
        || character.state === 'Crouch' || character.state === 'Guard');
  }

  private attackIdForInput(player: PlayerIndex, action: BufferedAction): string | undefined {
    return this.runtime.characters[player].isGrounded
      ? this.attackBindings[player][action]
      : this.airborneAttackBindings[player][action];
  }

  private getHurtbox(player: PlayerIndex): LocalAabb {
    const profile = this.hurtboxProfiles?.[player];
    if (profile === undefined) return this.hurtboxes[player];
    const character = this.runtime.characters[player];
    if (!character.isGrounded) return profile.airborne;
    if (character.state === 'Crouch') return profile.crouching;
    return profile.standing;
  }

  private applyScheduledAttackMovement(): void {
    for (const player of [0, 1] as const) {
      const character = this.runtime.characters[player];
      const instance = character.currentAttack;
      if (instance === null) continue;
      const definition = this.attackDefinitions.get(instance.attackId);
      if (definition === undefined) continue;
      const elapsed = instance.elapsedFrames;
      const facingSign = instance.startFacing === 'RIGHT' ? 1 : -1;
      for (const window of definition.movementWindows ?? []) {
        if (elapsed < window.startFrame || elapsed > window.endFrame) continue;
        character.position.x += window.displacementPerFrame.x * facingSign;
        character.position.y += window.displacementPerFrame.y;
      }
    }
  }

  private enqueueCinematic(player: PlayerIndex, attackInstanceId: string): void {
    const cinematicId = `cinematic-${this.battleGeneration}-${this.runtime.nextCinematicSequence}-p${player + 1}`;
    this.runtime.nextCinematicSequence += 1;
    this.runtime.pendingCinematics.push({ cinematicId, playerSlot: player, attackInstanceId });
  }

  private activatePendingCinematics(): boolean {
    if (this.runtime.activeCinematicId !== null || this.runtime.pendingCinematics.length === 0) return false;
    this.runtime.pendingCinematics.sort((left, right) => left.playerSlot - right.playerSlot);
    this.runtime.activeCinematicId = this.runtime.pendingCinematics[0]?.cinematicId ?? null;
    this.transitionBattlePhase('SUPER_CINEMATIC');
    const active = this.runtime.pendingCinematics[0];
    if (active !== undefined) this.emitCinematicStarted(active);
    return true;
  }

  private emitCinematicStarted(entry: BattleRuntimeState['pendingCinematics'][number]): void {
    const character = this.runtime.characters[entry.playerSlot];
    const attackId = character.currentAttack?.attackInstanceId === entry.attackInstanceId
      ? character.currentAttack.attackId : '';
    this.emit(BattleEventType.SUPER_CINEMATIC_STARTED, {
      cinematicId: entry.cinematicId,
      playerSlot: entry.playerSlot,
      attackId,
    });
  }

  private createCpuInput(player: PlayerIndex): InputFrame {
    const controller = this.cpuControllers[player];
    if (controller === null) return EMPTY_INPUT_FRAME;
    const character = this.runtime.characters[player];
    const opponentIndex: PlayerIndex = player === 0 ? 1 : 0;
    const opponent = this.runtime.characters[opponentIndex];
    const legalActions = new Set<CpuAction>(['IDLE']);
    const actionCapable = character.currentAttack === null
      && character.landingRecoveryFramesRemaining === 0
      && !['Attack', 'Special', 'Super', 'CounterAttack', 'Hitstun', 'Knockdown', 'KO'].includes(character.state);
    if (actionCapable) {
      legalActions.add('MOVE_FORWARD');
      legalActions.add('MOVE_BACK');
      if (character.isGrounded) {
        legalActions.add('JUMP');
        legalActions.add('CROUCH');
        legalActions.add('GUARD');
        const candidates = [
          ['LIGHT', 'light'], ['HEAVY', 'heavy'], ['SKILL_1', 'skill1'],
          ['SKILL_2', 'skill2'], ['SUPER', 'super'],
        ] as const;
        for (const [cpuAction, bufferedAction] of candidates) {
          const attackId = this.attackBindings[player][bufferedAction];
          if (attackId === undefined) continue;
          const definition = this.attackDefinitions.get(attackId);
          if (definition === undefined) continue;
          if (cpuAction === 'SUPER' && character.gauge < 100) continue;
          if (controller.profile.counterAction === cpuAction) {
            const opponentAttack = opponent.currentAttack === null
              ? undefined : this.attackDefinitions.get(opponent.currentAttack.attackId);
            if (opponentAttack === undefined || !opponentAttack.counterable || opponent.currentAttack === null
              || !['STARTUP', 'ACTIVE'].includes(getAttackPhase(opponentAttack, opponent.currentAttack.elapsedFrames))) continue;
          }
          if (controller.profile.trapAction === cpuAction) {
            const trapId = definition.effects.find((effect) => effect.type === 'SPAWN_TRAP')?.trapDefinitionId;
            const trap = trapId === undefined ? undefined : this.trapDefinitions.get(trapId);
            if (trap?.maxActivePerOwner !== undefined
              && this.runtime.combatTraps.filter((active) => active.ownerId === character.id
                && active.definitionId === trap.id).length >= trap.maxActivePerOwner) continue;
          }
          legalActions.add(cpuAction);
        }
      } else if (this.airborneAttackBindings[player].light !== undefined) {
        legalActions.add('LIGHT');
      }
    }
    return controller.step({
      selfState: character.state,
      facing: character.facing,
      distance: Math.abs(character.position.x - opponent.position.x),
      legalActions,
    }, this.randomSource);
  }

  private emit(type: BattleEvent['type'], payload: Readonly<Record<string, BattleEventValue>>): void {
    this.events.push(createBattleEvent(
      this.runtime.logicalFrame,
      this.nextEventSequence,
      type,
      payload,
    ));
    this.nextEventSequence += 1;
    if (this.combatDiagnostics !== null) this.combatDiagnostics.event(this.events[this.events.length - 1]!);
  }

  private assertPlayerIndex(player: number): asserts player is PlayerIndex {
    if (player !== 0 && player !== 1) throw new RangeError('player must be 0 or 1.');
  }
}
